import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { askAardvarkPromptGroups } from '../data/askAardvarkPrompts';
import { askAardvarkCopy } from '../utils/userFacingCopy';
import { successCopy } from '../utils/successCopy';
import { useSuccessSnackbar } from '../contexts/SuccessSnackbarContext';

interface AskAardvarkModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AskAardvarkModal({ open, onClose }: AskAardvarkModalProps) {
  const showSuccess = useSuccessSnackbar();
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const handleClose = () => {
    setCopiedKey(null);
    setCopyError(null);
    onClose();
  };

  const handleCopy = async (prompt: string, key: string) => {
    try {
      await navigator.clipboard.writeText(prompt);
      setCopiedKey(key);
      setCopyError(null);
      showSuccess(successCopy.askAardvarkPromptCopied);
      window.setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
    } catch {
      setCopyError(askAardvarkCopy.copyFailed);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="ask-aardvark-title"
    >
      <DialogTitle
        id="ask-aardvark-title"
        sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, pr: 1 }}
      >
        <Box>
          {askAardvarkCopy.dialogTitle}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400 }}>
            {askAardvarkCopy.intro}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {askAardvarkCopy.connectHint}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} aria-label={askAardvarkCopy.close} size="small" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {copyError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCopyError(null)}>
            {copyError}
          </Alert>
        )}
        {askAardvarkPromptGroups.map((group) => (
          <Box key={group.title} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {group.title}
            </Typography>
            {group.tip && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {group.tip}
              </Typography>
            )}
            <List disablePadding sx={{ mt: 1 }}>
              {group.prompts.map((prompt, index) => {
                const key = `${group.title}-${index}`;
                const justCopied = copiedKey === key;
                return (
                  <Box
                    component="li"
                    key={key}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      px: 1.5,
                      py: 1.25,
                      mb: 1,
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      bgcolor: 'grey.50',
                      listStyle: 'none',
                    }}
                  >
                    <Typography variant="body2" sx={{ flex: 1, pr: 1 }}>
                      {prompt}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon fontSize="small" />}
                      onClick={() => void handleCopy(prompt, key)}
                      sx={{ flexShrink: 0, textTransform: 'none' }}
                    >
                      {justCopied ? askAardvarkCopy.copied : askAardvarkCopy.copy}
                    </Button>
                  </Box>
                );
              })}
            </List>
          </Box>
        ))}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ pt: 1 }}>
          {askAardvarkCopy.readOnlyNote}
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
